import { NostrEvent } from "nostr-tools";
import { CommentFactory } from "applesauce-common/factories";
import { Comment as CommentCast } from "applesauce-common/casts";
import { CommentsModel } from "applesauce-common/models";
import { castTimelineStream } from "applesauce-common/observable";
import { createSignal, from, For, createEffect } from "solid-js";

import { asyncAction } from "../../../helpers/async-action";
import { accounts } from "../../../services/accounts";
import { publish } from "../../../services/pool";
import { formatTimeAgo } from "../../../helpers/date";
import { commentsLoader, reactionsLoader } from "../../../services/loaders";
import { appRelays } from "../../../services/lifestyle";
import UserAvatar from "../../../components/user-avatar";
import UserLink from "../../../components/user-link";
import { eventStore } from "../../../services/stores";

function AddCommentForm(props: { parent: NostrEvent }) {
  const [content, setContent] = createSignal("");

  const submit = asyncAction(async (e: SubmitEvent) => {
    e.preventDefault();
    if (content().length === 0) return;

    const signed = await CommentFactory.create(props.parent, content()).sign(
      accounts.signer,
    );
    publish(signed);

    (e.target as HTMLFormElement).reset();
  });

  if (submit.loading()) return <span class="loading loading-spinner"></span>;

  return (
    <form onSubmit={submit.run} class="mt-4">
      <textarea
        value={content()}
        onInput={(e) => setContent(e.target.value)}
        placeholder="Add a comment..."
        class="textarea textarea-bordered min-h-20 w-full"
        disabled={submit.loading()}
      />
      <button
        type="submit"
        class="btn btn-primary mt-2"
        disabled={submit.loading()}
      >
        Post Comment
      </button>
    </form>
  );
}

function CommentView(props: { comment: CommentCast; level?: number }) {
  const replies = from(props.comment.replies$);
  const lvl = props.level || 0;

  return (
    <>
      <div class={`border-b border-base-300 pb-3 pl-${lvl}rem`}>
        <div class="flex gap-2 items-center mb-2">
          <UserAvatar class="w-8 h-8" pubkey={props.comment.event.pubkey} />
          <UserLink class="font-medium" pubkey={props.comment.event.pubkey} />
          <span class="text-sm text-base-content/60 ms-auto">
            {formatTimeAgo(props.comment.event.created_at)}
          </span>
        </div>
        <p class="text-base-content/70">{props.comment.event.content}</p>
      </div>

      <For each={replies()}>
        {(comment) => <CommentView comment={comment} level={lvl + 1} />}
      </For>
    </>
  );
}

export default function WifiComments(props: { wifi: NostrEvent }) {
  const comments = from(
    eventStore
      .model(CommentsModel, props.wifi)
      .pipe(castTimelineStream(CommentCast, eventStore)),
  );

  // load the comments and reactions
  const relays = from(appRelays);
  createEffect(() => {
    commentsLoader({
      value: props.wifi.id,
      relays: relays(),
    }).subscribe();
    reactionsLoader(props.wifi, relays()).subscribe();
  });

  return (
    <div>
      <h2 class="text-xl font-semibold mb-4">Comments</h2>

      {/* Comment List */}
      <div class="space-y-4 mb-6">
        <For each={comments()}>
          {(comment) => <CommentView comment={comment} />}
        </For>
      </div>

      <AddCommentForm parent={props.wifi} />
    </div>
  );
}
